// conditionGenerator.js
// Utility for building JavaScript condition expressions used by CloudSam kernel.

/**
 * Generates a JavaScript condition expression string based on question type and builder inputs.
 * @param {Object} opts
 * @param {'yes_no'|'choice'|'number'|'scale'|'text'} opts.questionType
 * @param {string} opts.operator  Comparison operator (==, >=, etc.)
 * @param {string|number} opts.value  The literal value to compare against
 * @param {string} [opts.variableName='response'] Optional variable name for the left-hand side
 * @returns {string} JavaScript condition string
 */
export function generateCondition({ questionType, operator = '==', value, variableName = 'response' }) {
  const quoteIfString = (v) =>
    typeof v === 'string' && !/^\d+(\.\d+)?$/.test(v) ? `"${v}"` : v;

  const lhs = variableName || 'response';

  switch (questionType) {
    case 'yes_no':
    case 'choice':
    case 'text':
      return `${lhs} ${operator} ${quoteIfString(value)}`;
    case 'number':
    case 'scale':
      return `${lhs} ${operator} ${value}`;
    default:
      return `${lhs} ${operator} ${quoteIfString(value)}`;
  }
}

/**
 * Generate a range condition: min <= variable < max (with inclusive/exclusive toggles)
 * @param {Object} opts
 * @param {string} opts.variableName
 * @param {number|string} opts.min
 * @param {number|string} opts.max
 * @param {boolean} opts.minInclusive - true for >=, false for >
 * @param {boolean} opts.maxInclusive - true for <=, false for <
 * @returns {string} e.g. "variable >= 20 && variable < 40"
 */
export function generateRangeCondition({ variableName = 'response', min, max, minInclusive = true, maxInclusive = false }) {
  const lhs = variableName || 'response';
  const parts = [];
  if (min !== '' && min !== undefined && min !== null) {
    const op = minInclusive ? '>=' : '>';
    parts.push(`${lhs} ${op} ${min}`);
  }
  if (max !== '' && max !== undefined && max !== null) {
    const op = maxInclusive ? '<=' : '<';
    parts.push(`${lhs} ${op} ${max}`);
  }
  return parts.join(' && ') || `${lhs} >= 0`;
}

/**
 * Generate a multi-value OR condition: variable == "a" || variable == "b"
 * @param {Object} opts
 * @param {string} opts.variableName
 * @param {Array<string>} opts.values
 * @returns {string}
 */
export function generateMultiValueCondition({ variableName = 'response', values = [] }) {
  const lhs = variableName || 'response';
  if (!values.length) return '';
  return values
    .map(v => {
      const quoted = /^\d+(\.\d+)?$/.test(String(v)) ? v : `"${v}"`;
      return `${lhs} == ${quoted}`;
    })
    .join(' || ');
}

export function sanitizeLegacyExpression(expr = '') {
  if (!expr || typeof expr !== 'string') return expr;

  let sanitized = expr;

  sanitized = sanitized.replace(/(==|!=)\s*([A-Za-z_][A-Za-z0-9_-]*)/g, (_match, operator, literal) => {
    if (literal.startsWith('"') || literal.startsWith("'") || /^\d+(\.\d+)?$/.test(literal) || /^(true|false)$/i.test(literal)) {
      return `${operator} ${literal}`;
    }
    return `${operator} "${literal.toLowerCase()}"`;
  });

  return sanitized;
}